import React, { JSX, Suspense, useCallback, useEffect, useMemo, useState } from 'react'

import { useDisclosure, useTheme } from '@chakra-ui/react'
import {
    DownloadIcon,
    FontSizeIcon,
    LogoutIcon,
    MicrophoneIcon,
    RightChevronIcon,
    SearchIcon,
    VerticalDotsIcon,
    ClockIcon,
    PaintBrushIcon,
    EditTextIcon,
    LockIcon,
} from '@verbit-ai/icons-library'
import { useMediaRecorderContext } from '@verbit-ai/verbit-connect-web/recorder-provider'
import { useMediaPlayerContext } from '@verbit-ai/verbit-connect-web/player-provider'
import { Drawer } from '@verbit-ai/verbit-ui-library'

import { FontSizeControl } from 'src/components/FontSizeControl/FontSizeControl'
import { LanguageSelectMobile } from 'src/components/LanguageSelect/LanguageSelectMobile'
import { isLanguageSelectionAvailable } from 'src/models/TranscriptMetadata'
import { DisplaySettingsIcon, GlobeIcon } from 'src/components/icons'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { useEditor } from 'src/contexts/EditorContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useLogout } from 'src/contexts/UserProvider'
import { analyticsService } from 'src/services/AnalyticsService'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { TranscriptToolbarMicSelection } from 'src/components/TranscriptToolbar/TranscriptToolbarMicSelection'
import { ThemeToggleMobile } from 'src/components/ThemeToggle/ThemeToggleMobile'
import { Search } from 'src/components/Search/Search'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useIsTranscriptEmpty } from 'src/contexts/Transcript/TranscriptContext'
import { ToolbarLayout, getToolbarLayout } from 'src/components/TranscriptToolbar/types'
import { MediaPlayer, MediaPlayerToggle, MediaRecorder, MediaSource } from 'src/components/VerbitWrappers/VerbitConnect'
import { useDownloadOptions } from 'src/hooks/useDownloadOptions'
import { DownloadControl } from 'src/components/DownloadControl'
import { Switch } from 'src/components/VerbitWrappers/Switch'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'

import * as S from './styles'

const omitMediaRecorderParts = ['micSelection'] as const
const ICON_SIZE = '24px'
const OVERLAY_TRANSITION_DURATION = 300

// The switch's label re-dispatches each tap to its hidden input, so a tap bubbling out of the switch
// would reach the option's onClick twice on top of the switch's own onChange.
const stopSwitchClickPropagation = (event: React.MouseEvent) => event.stopPropagation()

type ToolbarMobileOption = {
    label: string
    ariaLabel: string
    icon: React.JSX.Element
    withSubmenu?: boolean
    onClick: () => void
    selectableControl?: React.JSX.Element
}

type ToolbarMobileStage = 'options' | 'micSelection'

export const TranscriptToolbarMobile = () => {
    const { containerRef } = useResponsiveContainer()

    const transcriptMetadata = useTranscriptMetadata()
    const canSelectLanguage = isLanguageSelectionAvailable(transcriptMetadata)
    const downloadOptions = useDownloadOptions()

    const { isMediaPlayerAvailable, isMediaRecorderAvailable, isMediaSourceAvailable } = useVerbitConnectAvailability()
    const { isMediaPlayerDisplayed } = useMediaPlayerContext()
    const shouldDisplayMediaPlayer = isMediaPlayerAvailable && isMediaPlayerDisplayed

    const layoutType = getToolbarLayout({ isMediaRecorderAvailable, isMediaPlayerAvailable, isMediaSourceAvailable })
    const isOnlyOptions = layoutType === 'noMedia'

    const isTranscriptEmpty = useIsTranscriptEmpty()
    const shouldShowSearchButton = !isTranscriptEmpty

    const [config, setProp] = useConfig()
    const isAllCapsDisplayAllowed = useFeatureFlag('allow_all_caps_display')
    const { isEditingLockAvailable, isEditingLocked, toggleEditingLock } = useEditor()

    const logout = useLogout()

    const { isOpen: isFontSizeControlOpen, onOpen: onFontSizeControlOpen, onClose: onFontSizeControlClose } = useDisclosure()
    const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure()
    const { isOpen: isThemeToggleOpen, onOpen: onThemeToggleOpen, onClose: onThemeToggleClose } = useDisclosure()
    const { isOpen: isLanguageSelectOpen, onOpen: onLanguageSelectOpen, onClose: onLanguageSelectClose } = useDisclosure()
    const { isOpen: isDownloadOpen, onOpen: onDownloadOpen, onClose: onDownloadClose } = useDisclosure()
    const { isOpen: isExtraMenuOpen, onOpen: onExtraMenuOpen, onClose: onExtraMenuClose } = useDisclosure()

    const {
        palette: {
            toolbar: { mobilePanelIconColor, extrasIconColor },
        },
    } = useTheme()
    const { audioInputDevices, selectedAudioInput } = useMediaRecorderContext()

    const onOverlayClose = useCallback(() => {
        onExtraMenuClose()
        setStage('options')
    }, [onExtraMenuClose])

    const onLockEditingClick = useCallback(() => {
        toggleEditingLock('mobile_menu')
    }, [toggleEditingLock])

    const toggleTimestamps = useCallback(() => {
        setProp('isTimestampFormatsEnabled', (isEnabled) => !isEnabled)
    }, [setProp])

    const toggleShowSpeakerColorCode = useCallback(() => {
        setProp('isShowSpeakerColorCodeEnabled', (isEnabled) => !isEnabled)
    }, [setProp])

    const toggleShowTranscriptInAllCaps = useCallback(() => {
        setProp('shouldShowTranscriptInAllCaps', (isEnabled) => !isEnabled)
    }, [setProp])

    const inlineOptions = useMemo<ToolbarMobileOption[]>(() => {
        const options: ToolbarMobileOption[] = [
            {
                label: 'Extras',
                ariaLabel: 'extras',
                icon: <VerticalDotsIcon size={ICON_SIZE} color={mobilePanelIconColor} />,
                onClick: onExtraMenuOpen,
            },
        ]

        if (isOnlyOptions) {
            if (shouldShowSearchButton) {
                options.push({
                    label: 'Search in transcript',
                    ariaLabel: 'search',
                    icon: <SearchIcon size={ICON_SIZE} color={mobilePanelIconColor} />,
                    onClick: onSearchOpen,
                })
            }

            options.push(
                {
                    label: 'Theme',
                    ariaLabel: 'theme',
                    icon: <DisplaySettingsIcon width={ICON_SIZE} height={ICON_SIZE} color={mobilePanelIconColor} />,
                    onClick: onThemeToggleOpen,
                },
                {
                    label: 'Font size',
                    ariaLabel: 'font-size',
                    icon: <FontSizeIcon size={ICON_SIZE} color={mobilePanelIconColor} />,
                    onClick: onFontSizeControlOpen,
                },
            )

            if (canSelectLanguage) {
                options.push({
                    label: 'Select language',
                    ariaLabel: 'select-language',
                    icon: <GlobeIcon width={ICON_SIZE} height={ICON_SIZE} color={mobilePanelIconColor} />,
                    onClick: onLanguageSelectOpen,
                })
            }
        }

        return options
    }, [
        canSelectLanguage,
        isOnlyOptions,
        mobilePanelIconColor,
        onExtraMenuOpen,
        onFontSizeControlOpen,
        onLanguageSelectOpen,
        onSearchOpen,
        onThemeToggleOpen,
        shouldShowSearchButton,
    ])

    const extraOptions = useMemo<ToolbarMobileOption[]>(() => {
        const options: ToolbarMobileOption[] = []
        const hasMultipleMicsAvailable = audioInputDevices?.length > 1

        if (layoutType !== 'noMedia') {
            if (shouldShowSearchButton) {
                options.push({
                    label: 'Search in transcript',
                    ariaLabel: 'search',
                    icon: <SearchIcon size={ICON_SIZE} color={extrasIconColor} />,
                    onClick: () => {
                        onSearchOpen()
                        onOverlayClose()
                    },
                })
            }

            if (layoutType === 'mediaRecorder' || layoutType === 'mediaRecorderAndPlayer') {
                options.push({
                    label: selectedAudioInput?.label || 'Select a microphone',
                    ariaLabel: 'select-microphone',
                    icon: <MicrophoneIcon size={ICON_SIZE} color={extrasIconColor} />,
                    withSubmenu: hasMultipleMicsAvailable,
                    onClick: () => {
                        if (hasMultipleMicsAvailable) {
                            setStage('micSelection')
                        }
                    },
                })
            }

            options.push(
                {
                    label: 'Theme',
                    ariaLabel: 'theme',
                    icon: <DisplaySettingsIcon width={ICON_SIZE} height={ICON_SIZE} color={extrasIconColor} />,
                    withSubmenu: true,
                    onClick: () => {
                        onExtraMenuClose()
                        setTimeout(onThemeToggleOpen, OVERLAY_TRANSITION_DURATION)
                    },
                },
                {
                    label: 'Font size',
                    ariaLabel: 'font-size',
                    icon: <FontSizeIcon size={ICON_SIZE} color={extrasIconColor} />,
                    withSubmenu: true,
                    onClick: () => {
                        onExtraMenuClose()
                        setTimeout(onFontSizeControlOpen, OVERLAY_TRANSITION_DURATION)
                    },
                },
            )
            if (canSelectLanguage) {
                options.push({
                    label: 'Select language',
                    ariaLabel: 'select-language',
                    icon: <GlobeIcon width={ICON_SIZE} height={ICON_SIZE} color={extrasIconColor} />,
                    onClick: () => {
                        onExtraMenuClose()
                        setTimeout(onLanguageSelectOpen, OVERLAY_TRANSITION_DURATION)
                    },
                })
            }
        }

        if (downloadOptions.length === 1) {
            options.push({
                label: downloadOptions[0].labelStandalone,
                ariaLabel: 'download',
                icon: <DownloadIcon size={ICON_SIZE} color={extrasIconColor} />,
                onClick: downloadOptions[0].action,
            })
        }
        if (downloadOptions.length > 1) {
            options.push({
                label: 'Download',
                ariaLabel: 'download',
                icon: <DownloadIcon size={ICON_SIZE} color={extrasIconColor} />,
                withSubmenu: true,
                onClick: () => {
                    onExtraMenuClose()
                    setTimeout(onDownloadOpen, OVERLAY_TRANSITION_DURATION)
                },
            })
        }

        if (transcriptMetadata?.viewMode === 'job') {
            if (isEditingLockAvailable) {
                options.push({
                    label: 'Lock editing',
                    ariaLabel: 'lock-editing',
                    icon: <LockIcon size={ICON_SIZE} color={extrasIconColor} />,
                    selectableControl: (
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={isEditingLocked}
                                onChange={onLockEditingClick}
                                ariaLabel="lock-editing"
                                data-appcues-lock-editing-switch="lock-editing-switch"
                            />
                        </S.SwitchWrapper>
                    ),
                    onClick: onLockEditingClick,
                })
            }

            options.push(
                {
                    label: 'Show timestamp',
                    ariaLabel: 'timestamp-formats',
                    icon: <ClockIcon size={ICON_SIZE} color={extrasIconColor} />,
                    selectableControl: (
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={config.isTimestampFormatsEnabled}
                                onChange={toggleTimestamps}
                                ariaLabel="timestamp-formats"
                                data-appcues-timestamp-formats-switch="timestamp-formats-switch"
                            />
                        </S.SwitchWrapper>
                    ),
                    onClick: toggleTimestamps,
                },
                {
                    label: 'Show speaker color code',
                    ariaLabel: 'show-speaker-color-code',
                    icon: <PaintBrushIcon size={ICON_SIZE} color={extrasIconColor} />,
                    selectableControl: (
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={config.isShowSpeakerColorCodeEnabled}
                                onChange={toggleShowSpeakerColorCode}
                                ariaLabel="show-speaker-color-code"
                                data-appcues-show-speaker-color-code-switch="show-speaker-color-code-switch"
                            />
                        </S.SwitchWrapper>
                    ),
                    onClick: toggleShowSpeakerColorCode,
                },
            )

            if (isAllCapsDisplayAllowed) {
                options.push({
                    label: 'Show transcript in ALL CAPS',
                    ariaLabel: 'show-transcript-in-all-caps',
                    icon: <EditTextIcon size={ICON_SIZE} color={extrasIconColor} />,
                    selectableControl: (
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={config.shouldShowTranscriptInAllCaps}
                                onChange={toggleShowTranscriptInAllCaps}
                                ariaLabel="show-transcript-in-all-caps"
                                data-appcues-show-transcript-in-all-caps-switch="show-transcript-in-all-caps-switch"
                            />
                        </S.SwitchWrapper>
                    ),
                    onClick: toggleShowTranscriptInAllCaps,
                })
            }
        }

        options.push({
            label: 'Log Out',
            ariaLabel: 'sign-out',
            icon: <LogoutIcon size={ICON_SIZE} color={extrasIconColor} />,
            onClick: logout,
        })

        return options
    }, [
        audioInputDevices?.length,
        layoutType,
        downloadOptions,
        transcriptMetadata?.viewMode,
        extrasIconColor,
        logout,
        shouldShowSearchButton,
        canSelectLanguage,
        onSearchOpen,
        onOverlayClose,
        selectedAudioInput?.label,
        onExtraMenuClose,
        onThemeToggleOpen,
        onFontSizeControlOpen,
        onLanguageSelectOpen,
        onDownloadOpen,
        config.isTimestampFormatsEnabled,
        config.isShowSpeakerColorCodeEnabled,
        config.shouldShowTranscriptInAllCaps,
        toggleTimestamps,
        toggleShowSpeakerColorCode,
        toggleShowTranscriptInAllCaps,
        isAllCapsDisplayAllowed,
        isEditingLockAvailable,
        isEditingLocked,
        onLockEditingClick,
    ])

    const layoutsMap: Record<ToolbarLayout, JSX.Element> = {
        mediaRecorderAndPlayer: (
            <>
                <Suspense fallback={<S.Spacer />}>
                    <S.MediaPlayerToggleWrapper>
                        <MediaPlayerToggle />
                    </S.MediaPlayerToggleWrapper>
                    <MediaRecorder omitLayoutParts={omitMediaRecorderParts} />
                </Suspense>
                {shouldDisplayMediaPlayer && (
                    <Suspense>
                        <S.MediaPlayerWrapper>
                            <MediaPlayer />
                        </S.MediaPlayerWrapper>
                    </Suspense>
                )}
            </>
        ),
        mediaSourceAndPlayer: (
            <>
                <Suspense fallback={<S.Spacer />}>
                    <S.MediaPlayerToggleWrapper>
                        <MediaPlayerToggle />
                    </S.MediaPlayerToggleWrapper>
                    <MediaSource />
                </Suspense>
                {shouldDisplayMediaPlayer && (
                    <Suspense>
                        <S.MediaPlayerWrapper>
                            <MediaPlayer />
                        </S.MediaPlayerWrapper>
                    </Suspense>
                )}
            </>
        ),
        mediaRecorder: (
            <Suspense fallback={<S.Spacer />}>
                <MediaRecorder omitLayoutParts={omitMediaRecorderParts} />
            </Suspense>
        ),
        mediaPlayer: (
            <Suspense>
                <S.MediaPlayerWrapper>
                    <MediaPlayer canExitPlayback={false} />
                </S.MediaPlayerWrapper>
            </Suspense>
        ),
        mediaSource: (
            <Suspense fallback={<S.Spacer />}>
                <MediaSource />
            </Suspense>
        ),
        noMedia: <></>,
    }

    const [stage, setStage] = useState<ToolbarMobileStage>('options')
    const extraOptionsTemplatesByStage: Record<ToolbarMobileStage, React.JSX.Element> = {
        options: (
            <>
                {extraOptions.map(({ label, ariaLabel, icon, withSubmenu, onClick, selectableControl }) => (
                    <S.ExtraMenuOption key={ariaLabel} aria-label={ariaLabel} onClick={onClick}>
                        {icon}
                        <S.ExtraMenuOptionLabel>{label}</S.ExtraMenuOptionLabel>
                        {withSubmenu && <RightChevronIcon className={S.EXTRA_CHEVRON_CLASSNAME} size={18} color={extrasIconColor} />}
                        {selectableControl}
                    </S.ExtraMenuOption>
                ))}
            </>
        ),
        micSelection: <TranscriptToolbarMicSelection />,
    }

    useEffect(() => {
        if (isLanguageSelectOpen) {
            analyticsService.track('TranslationToggled', {})
        }
    }, [isLanguageSelectOpen])

    return (
        <S.Container>
            <S.Panel $isOnlyOptions={isOnlyOptions}>
                {inlineOptions.map(({ ariaLabel, icon, onClick }) => (
                    <S.InlineOption
                        $isOnlyOptions={isOnlyOptions}
                        key={ariaLabel}
                        aria-label={ariaLabel}
                        variant="link"
                        icon={icon}
                        onClick={onClick}
                    />
                ))}
                {layoutsMap[layoutType]}
            </S.Panel>

            <Drawer isOpen={isExtraMenuOpen} onClose={onOverlayClose} placement="bottom" autoFocus={false} portalProps={{ containerRef }}>
                <S.ExtraMenuContainer>{extraOptionsTemplatesByStage[stage]}</S.ExtraMenuContainer>
            </Drawer>

            <ThemeToggleMobile isOpen={isThemeToggleOpen} onOpen={onThemeToggleOpen} onClose={onThemeToggleClose} />
            <FontSizeControl isOpen={isFontSizeControlOpen} onOpen={onFontSizeControlOpen} onClose={onFontSizeControlClose} />
            <LanguageSelectMobile isOpen={isLanguageSelectOpen} onOpen={onLanguageSelectOpen} onClose={onLanguageSelectClose} />
            <DownloadControl isOpen={isDownloadOpen} onOpen={onDownloadOpen} onClose={onDownloadClose} options={downloadOptions} />
            {shouldShowSearchButton && <Search isOpen={isSearchOpen} onOpen={onSearchOpen} onClose={onSearchClose} />}
        </S.Container>
    )
}
